class ApplyWithCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.track = this.querySelector('[data-apply-with-track]');
    this.prevButton = this.querySelector('[data-apply-with-prev]');
    this.nextButton = this.querySelector('[data-apply-with-next]');
    this.dotsWrap = this.querySelector('[data-apply-with-dots]');
    this.slides = Array.from(this.querySelectorAll('.product-apply-with__slide'));

    this.prevButton?.addEventListener('click', () => this.scrollBySlide(-1));
    this.nextButton?.addEventListener('click', () => this.scrollBySlide(1));
    this.track?.addEventListener('scroll', () => this.syncControls(), { passive: true });

    this.querySelectorAll('[data-apply-with-form]').forEach((form) => {
      form.addEventListener('submit', (event) => this.handleAddToCart(event));
    });

    this.buildDots();
    this.syncControls();

    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => this.syncControls());
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  slideWidth() {
    const slide = this.slides[0];
    if (!slide || !this.track) return this.track?.clientWidth || 0;
    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  currentIndex() {
    if (!this.track) return 0;
    const width = this.slideWidth();
    if (!width) return 0;
    return Math.round(this.track.scrollLeft / width);
  }

  scrollBySlide(direction) {
    if (!this.track) return;
    this.track.scrollBy({ left: direction * this.slideWidth(), behavior: 'smooth' });
  }

  scrollToIndex(index) {
    if (!this.track) return;
    this.track.scrollTo({ left: index * this.slideWidth(), behavior: 'smooth' });
  }

  buildDots() {
    if (!this.dotsWrap || this.slides.length < 2) return;

    this.dotsWrap.hidden = false;
    this.dotsWrap.innerHTML = '';

    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'product-apply-with__dot';
      dot.setAttribute('aria-label', `Go to product ${index + 1}`);
      dot.addEventListener('click', () => this.scrollToIndex(index));
      this.dotsWrap.appendChild(dot);
    });
  }

  syncControls() {
    const index = this.currentIndex();
    const maxIndex = Math.max(this.slides.length - 1, 0);

    if (this.prevButton) this.prevButton.disabled = index <= 0;
    if (this.nextButton) this.nextButton.disabled = index >= maxIndex;

    this.dotsWrap?.querySelectorAll('.product-apply-with__dot').forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === index);
    });
  }

  async handleAddToCart(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const button = form.querySelector('.product-apply-with__atc');
    const variantId = button?.dataset.variantId || form.querySelector('[name="id"]')?.value;

    if (!variantId || !button || button.disabled) return;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    button.classList.remove('is-added');

    try {
      const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
        }),
      });

      const data = await response.json();
      if (data.status) {
        throw new Error(data.description || data.message || 'Could not add to cart.');
      }

      button.textContent = 'Added';
      button.classList.add('is-added');

      if (typeof window.refreshCartUI === 'function') {
        await window.refreshCartUI({ openDrawer: true });
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      window.setTimeout(() => {
        button.textContent = originalLabel;
        button.classList.remove('is-added');
        button.disabled = false;
      }, 1600);
    } catch (error) {
      button.textContent = originalLabel;
      button.disabled = false;
      console.warn('Apply-with add to cart failed', error);
    }
  }
}

if (!customElements.get('apply-with-carousel')) {
  customElements.define('apply-with-carousel', ApplyWithCarousel);
}